(() => {
  if (customElements.get('n-fantasia-interest')) return;

  class NFantasiaInterest extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-n-fantasia-interest-dialog]');
      this.openButton = this.querySelector('[data-n-fantasia-interest-open]');
      this.closeButton = this.querySelector('[data-n-fantasia-interest-close]');
      this.availabilityNote = this.querySelector('[data-n-fantasia-interest-note]');
      this.form = this.querySelector('form');
      this.tagsInput = this.querySelector('[data-n-interest-tags]');
      this.quantityInput = this.querySelector('[data-n-interest-quantity]');
      this.emailInput = this.querySelector('input[type="email"]');
      this.consentInput = this.querySelector('[data-n-interest-consent]');
      this.databaseError = this.querySelector('[data-n-interest-database-error]');
      this.submitButton = this.querySelector('[data-n-interest-submit]');
      this.nativeBuy = this.closest('[data-n-primary-buy-actions]')?.querySelector('[data-n-native-buy-buttons]');
      this.variants = this.readVariants();
      this.lastFocusedElement = null;
      this.isSubmitting = false;
      this.submitButtonLabel = this.submitButton?.textContent.trim() || '';

      this.openButton?.addEventListener('click', () => this.open());
      this.closeButton?.addEventListener('click', () => this.close());
      this.dialog?.addEventListener('click', (event) => this.handleBackdropClick(event));
      this.form?.addEventListener('submit', this.handleSubmit);
      this.quantityInput?.addEventListener('input', () => this.updateTags());
      this.quantityInput?.addEventListener('change', () => this.updateTags());
      document.addEventListener('change', this.handleDocumentChange);
      window.addEventListener('pageshow', this.handlePageShow);

      this.buttonObserver = new MutationObserver(() => this.syncState());
      this.observeNativeButton();
      this.syncState();

      if (this.querySelector('[data-n-fantasia-interest-success], [data-n-interest-errors]')) {
        requestAnimationFrame(() => this.open(false));
      }

      if (this.querySelector('[data-n-fantasia-interest-success]')) {
        this.markRegistered();
        this.publish('n_fantasia_interest_submitted');
      }
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.handleDocumentChange);
      window.removeEventListener('pageshow', this.handlePageShow);
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.buttonObserver?.disconnect();
    }

    handleDocumentChange = (event) => {
      const productInfo = this.closest('product-info');
      if (!productInfo || !productInfo.contains(event.target)) return;

      requestAnimationFrame(() => {
        this.observeNativeButton();
        this.syncState();
      });
    };

    handlePageShow = () => {
      this.resetSubmissionState();
    };

    handleSubmit = async (event) => {
      event.preventDefault();
      if (this.isSubmitting) return;

      this.isSubmitting = true;
      this.updateTags();
      this.showDatabaseError('');
      this.setSubmissionState();
      this.publish('n_fantasia_interest_submit_started');

      try {
        await this.submitToDatabase();
        HTMLFormElement.prototype.submit.call(this.form);
      } catch (error) {
        console.error('Impossibile registrare l’interesse per la fantasia.', error);
        this.resetSubmissionState();
        this.showDatabaseError('Non è stato possibile registrare la richiesta. Riprova tra poco.');
      }
    };

    readVariants() {
      try {
        return JSON.parse(this.querySelector('[data-n-interest-variants]')?.textContent || '[]');
      } catch (error) {
        console.warn('Impossibile leggere le varianti del modulo fantasia.', error);
        return [];
      }
    }

    getNativeButton() {
      return this.nativeBuy?.querySelector('button[name="add"], input[name="add"], .product-form__submit') || null;
    }

    getSelectedVariantId() {
      const productInfo = this.closest('product-info');
      const idInput = productInfo?.querySelector('form[action*="/cart/add"] [name="id"]');
      return idInput?.value || this.variants[0]?.id?.toString() || '';
    }

    getSelectedVariant() {
      const selectedId = this.getSelectedVariantId();
      return this.variants.find((variant) => variant.id.toString() === selectedId.toString()) || this.variants[0];
    }

    observeNativeButton() {
      const nativeButton = this.getNativeButton();
      if (!nativeButton || nativeButton === this.observedButton) return;

      this.buttonObserver.disconnect();
      this.observedButton = nativeButton;
      this.buttonObserver.observe(nativeButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });
    }

    syncState() {
      const interestMode = true;

      if (this.nativeBuy) this.nativeBuy.hidden = interestMode;
      if (this.openButton) this.openButton.hidden = !interestMode;
      if (this.availabilityNote) this.availabilityNote.hidden = !interestMode;

      this.updateTags();
      if (this.dataset.cardMode !== 'true') {
        this.updateStickyButton(interestMode);
        document.dispatchEvent(new CustomEvent('n:product-primary-action-change'));
      }
    }

    updateTags() {
      if (!this.tagsInput) return;

      const variant = this.getSelectedVariant();
      const quantity = Math.max(1, Math.min(20, Number.parseInt(this.quantityInput?.value || '1', 10) || 1));
      const tags = [
        this.dataset.baseTags,
        variant?.id ? `interesse-variante-${variant.id}` : '',
        `richiesta-${this.dataset.productId}-q${quantity}`,
      ].filter(Boolean);

      this.tagsInput.value = tags.join(',');
    }

    updateStickyButton(unavailable) {
      const stickyButton = document.querySelector('[data-sticky-product-add]');
      if (!stickyButton) return;

      stickyButton.dataset.nInterestMode = unavailable ? 'true' : 'false';
      stickyButton.textContent = unavailable
        ? this.dataset.interestLabel || 'Avvisami quando disponibile'
        : stickyButton.dataset.addLabel || this.dataset.addLabel || 'Aggiungi al carrello';
    }

    setSubmissionState() {
      if (!this.submitButton) return;

      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitButton.textContent = this.submitButton.dataset.loadingLabel || 'Registrazione in corso…';
    }

    resetSubmissionState() {
      this.isSubmitting = false;
      if (!this.submitButton) return;

      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');
      if (this.submitButtonLabel) this.submitButton.textContent = this.submitButtonLabel;
    }

    markRegistered() {
      if (!this.openButton) return;

      const label = this.dataset.registeredLabel || 'Richiesta registrata ✓';
      const cardLabel = this.openButton.querySelector('.n-fantasia-interest__card-label');
      if (cardLabel) {
        cardLabel.textContent = label;
      } else if (this.dataset.cardMode === 'true') {
        this.openButton.setAttribute('aria-label', label);
      } else {
        this.openButton.textContent = label;
      }
    }

    async submitToDatabase() {
      const baseUrl = (this.dataset.supabaseUrl || '').replace(/\/$/, '');
      const apiKey = this.dataset.supabaseKey || '';
      const variant = this.getSelectedVariant();
      const quantity = Math.max(1, Math.min(20, Number.parseInt(this.quantityInput?.value || '1', 10) || 1));
      const params = new URLSearchParams(window.location.search);

      if (!/^https:\/\/[a-z0-9-]+\.supabase\.co$/i.test(baseUrl) || !apiKey || !variant?.id) {
        throw new Error('Configurazione Supabase non valida.');
      }

      const response = await fetch(`${baseUrl}/rest/v1/rpc/lyoss_submit_pattern_interest`, {
        method: 'POST',
        headers: {
          apikey: apiKey,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          p_email: this.emailInput?.value.trim() || '',
          p_product_id: Number(this.dataset.productId),
          p_product_handle: this.dataset.productHandle || '',
          p_product_title: this.dataset.productTitle || '',
          p_variant_id: Number(variant.id),
          p_variant_title: variant.title || '',
          p_quantity: quantity,
          p_email_consent: Boolean(this.consentInput?.checked),
          p_page_url: window.location.href,
          p_utm_source: params.get('utm_source'),
          p_utm_medium: params.get('utm_medium'),
          p_utm_campaign: params.get('utm_campaign'),
          p_utm_content: params.get('utm_content'),
          p_utm_term: params.get('utm_term'),
        }),
      });

      const result = await response.json().catch(() => null);
      if (!response.ok || !Array.isArray(result) || !result[0]?.response_id) {
        throw new Error(result?.message || 'Supabase non ha confermato il salvataggio.');
      }

      return result[0];
    }

    showDatabaseError(message) {
      if (!this.databaseError) return;
      this.databaseError.textContent = message;
      this.databaseError.hidden = !message;
    }

    open(track = true) {
      if (!this.dialog) return;

      this.lastFocusedElement = document.activeElement;
      if (!this.dialog.open) this.dialog.showModal();

      const focusTarget = this.querySelector('[data-n-fantasia-interest-success], [data-n-interest-errors], input[type="email"]');
      requestAnimationFrame(() => focusTarget?.focus({ preventScroll: true }));

      if (track) this.publish('n_fantasia_interest_opened');
    }

    close() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      this.lastFocusedElement?.focus?.({ preventScroll: true });
    }

    handleBackdropClick(event) {
      if (event.target === this.dialog) this.close();
    }

    publish(name) {
      const variant = this.getSelectedVariant();
      const payload = {
        source: this.dataset.source || 'product-page',
        product_id: this.dataset.productId,
        product_handle: this.dataset.productHandle,
        product_title: this.dataset.productTitle,
        variant_id: variant?.id || null,
        variant_title: variant?.title || null,
        quantity: Number.parseInt(this.quantityInput?.value || '1', 10) || 1,
      };

      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(name, payload);
      }

      this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: payload }));
    }
  }

  customElements.define('n-fantasia-interest', NFantasiaInterest);
})();
